import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { ColumnDef } from '@tanstack/react-table'
import React, { useEffect, useMemo, useState } from 'react'
import Countdown from 'react-countdown'
import { useQuery } from 'react-query'

import { format, isAfter, isBefore, parseISO } from 'date-fns'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'

import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { Layout } from '@/pages/customer/layout'
import userService from '@/services/user-service'

import { TaskSquareIcon } from '@/assets/icons'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { toZonedTime } from 'date-fns-tz'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'

export const Bookings = () => {
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')

	const { data, isLoading, refetch } = useQuery(
		'fetch-user-bookings',
		() =>
			userService.fetchUserBookings({
				pageNumber: '1',
				pageSize: rowsPerPage.toString(),
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching user bookings')
			},
		},
	)

	const statusPriority = {
		Aktiv: 0,
		Offen: 1,
		Beendet: 2,
		Storniert: 3,
	}

	const sortedBookings = data?.sort((a, b) => {
		const now = new Date()

		const getStatus = (booking: UserBooking) => {
			const startDate = toZonedTime(new Date(booking.start), 'UTC')
			const endDate = toZonedTime(new Date(booking.end), 'UTC')

			if (booking.status === '2') {
				return 'Storniert'
			} else if (isAfter(startDate, now)) {
				return 'Offen'
			} else if (isBefore(endDate, now)) {
				return 'Beendet'
			} else {
				return 'Aktiv'
			}
		}

		const aStatus = getStatus(a)
		const bStatus = getStatus(b)

		const statusDiff = statusPriority[aStatus] - statusPriority[bStatus]
		if (statusDiff !== 0) return statusDiff

		const aStart = parseISO(a.createdAt)
		const bStart = parseISO(b.createdAt)
		return bStart.getTime() - aStart.getTime()
	})

	const sortedData = useMemo(() => {
		if (!sortedBookings) return []
		if (!sortColumn) return sortedBookings

		return [...sortedBookings].sort((a, b) => {
			const aValue = a[sortColumn as keyof UserBooking]
			const bValue = b[sortColumn as keyof UserBooking]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [sortedBookings, sortColumn, sortDirection])

	useEffect(() => {
		refetch()
	}, [rowsPerPage])

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const columns: ColumnDef<UserBooking>[] = [
		{
			id: 'store',
			accessorFn: (row: UserBooking) => row.store?.name || '—',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Store" />,
		},
		{
			accessorKey: 'stand_id',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Regal" />,
		},
		{
			accessorKey: 'duration',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Dauer" />,
		},
		{
			accessorKey: 'start',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Mietbeginn" />,
			cell: ({ row }) => (
				<div>{format(toZonedTime(new Date(row.getValue('start')), 'UTC'), 'dd-MM-yyyy')}</div>
			),
		},
		{
			accessorKey: 'end',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Mietende" />,
			cell: ({ row }) => (
				<div>{format(toZonedTime(new Date(row.getValue('end')), 'UTC'), 'dd-MM-yyyy')}</div>
			),
		},
		{
			accessorKey: 'rent_price',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Preis" />,
		},
		{
			accessorKey: 'status',
			accessorFn: (row: UserBooking) => {
				const now = new Date()
				const startDate = toZonedTime(new Date(row.start), 'UTC')
				const endDate = toZonedTime(new Date(row.end), 'UTC')

				if (row.status === '2') return 'Storniert'
				if (isAfter(startDate, now)) return 'Offen'
				if (isBefore(endDate, now)) return 'Beendet'
				return 'Aktiv'
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						row.getValue('status') === 'Aktiv'
							? 'bg-[#61B9641A] text-[#61B964]'
							: row.getValue('status') === 'Beendet'
								? 'bg-gray-100 text-gray-500'
								: row.getValue('status') === 'Offen'
									? 'bg-blue-100 text-blue-600'
									: 'bg-[#FFEFF2] text-[#DB5358]',
					)}>
					{row.getValue('status')}
				</div>
			),
		},
		{
			accessorKey: 'end',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Mietende in" />,
			cell: ({ row }) => <Countdown date={new Date(row.getValue('end'))} />,
		},
	]

	const MobileUserBookingsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('stand_id')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Regal
								{sortColumn === 'stand_id' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('status')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Status
								{sortColumn === 'status' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('end')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Mietende
								{sortColumn === 'end' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedData?.map((booking, index) => {
						const now = new Date()
						const startDate = toZonedTime(new Date(booking.start), 'UTC')
						const endDate = toZonedTime(new Date(booking.end), 'UTC')

						let status: string
						if (booking.status === '2') {
							status = 'Storniert'
						} else if (isAfter(startDate, now)) {
							status = 'Offen'
						} else if (isBefore(endDate, now)) {
							status = 'Beendet'
						} else {
							status = 'Aktiv'
						}
						return (
							<React.Fragment key={booking.id.toString() + index.toString()}>
								<TableRow
									className={cn(
										'h-14',
										expandedRows.includes(booking.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
									)}
									onClick={() => toggleRow(booking.id)}>
									<TableCell
										align="center"
										className={cn('text-xs', {
											'text-muted-foreground': !expandedRows.includes(booking.id),
										})}>
										<div className="flex items-center">
											<ChevronRightIcon
												className={cn(
													'size-3 shrink-0 -mt-0.5',
													expandedRows.includes(booking.id)
														? 'rotate-90 text-secondary'
														: 'text-muted-foreground',
												)}
											/>
											{booking.stand_id}
										</div>
									</TableCell>
									<TableCell
										align="center"
										className={cn('text-[10px]', {
											'text-muted-foreground': !expandedRows.includes(booking.id),
										})}>
										<div
											className={cn(
												'text-[10px] w-fit p-1.5 px-2 rounded-[18px]',
												status === 'Aktiv'
													? 'bg-[#61B9641A] text-[#61B964]'
													: status === 'Offen'
														? 'bg-blue-100 text-blue-600'
														: status === 'Beendet'
															? 'bg-gray-100 text-gray-500'
															: 'bg-[#FFEFF2] text-[#DB5358]',
											)}>
											{status}
										</div>
									</TableCell>
									<TableCell
										align="center"
										className={cn('text-xs', {
											'text-muted-foreground': !expandedRows.includes(booking.id),
										})}>
										{format(toZonedTime(new Date(booking.end), 'UTC'), 'dd-MM-yyyy')}
									</TableCell>
								</TableRow>
								{expandedRows.includes(booking.id) && (
									<TableRow
										className={cn(
											'mt-2',
											!expandedRows.includes(booking.id)
												? '!border-b-0'
												: 'border-b border-[#F4F4F4]',
										)}>
										<TableCell className="pt-0" colSpan={3}>
											<div className="px-3 flex flex-col gap-4">
												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">
														Store
													</h6>
													<p className="text-secondary text-xs font-medium">
														{booking.store?.name || '—'}
													</p>
												</div>

												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">
														Mietbeginn
													</h6>
													<p className="text-secondary text-xs font-medium">
														{format(toZonedTime(new Date(booking.start), 'UTC'), 'dd-MM-yyyy')}
													</p>
												</div>

												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">Dauer</h6>
													<p className="text-secondary text-xs font-medium">{booking.duration}</p>
												</div>

												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">Preis</h6>
													<p className="text-secondary text-xs font-medium">{booking.rent_price}</p>
												</div>
												<div className="flex items-center justify-between">
													<h6 className="text-muted-foreground text-[10px] font-medium">
														Mietende in
													</h6>
													<Countdown
														className="text-xs text-secondary font-medium"
														date={new Date(booking.end)}
													/>
												</div>
											</div>
										</TableCell>
									</TableRow>
								)}
							</React.Fragment>
						)
					})}
				</TableBody>
			</Table>
		)
	}

	return (
		<Layout title="Meine Buchungen">
			<div className="mt-3 rounded-xl lg:rounded-[16px] px-3 py-4 sm:px-[18px] sm:py-5 bg-white flex flex-col gap-4 min-w-0">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2 lg:gap-3">
						<div className="rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
							<TaskSquareIcon className="text-secondary size-5 lg:size-6 shrink-0" />
						</div>
						<h1 className="text-secondary text-xs lg:text-base font-extrabold">
							Übersicht deiner Buchungen
						</h1>
					</div>
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-[133px] h-12 text-xs">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500, 'alle'].map(pageSize => (
								<SelectItem
									key={pageSize}
									value={pageSize === 'alle' ? '10000' : pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				{isSmallScreen ? (
					<MobileUserBookingsTable />
				) : (
					<DataTable columns={columns} data={sortedData ?? []} isLoading={isLoading} />
				)}
			</div>
		</Layout>
	)
}
